import { useT } from '@transifex/react';
import { useEffect, useMemo, useState, type JSX } from 'react';

import type { AuthOptions } from 'pkg/actions/auth';

import * as actions from 'pkg/actions';
import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import { useEndpoint } from 'pkg/api/use_endpoint';
import { useQueryParams } from 'pkg/hooks/query_state';
import { useOrganizationIdentity } from 'pkg/identity/organization';
import { parse } from 'pkg/json';
import { LoginTypes, storeLoginInfo } from 'pkg/local_storage/login-info';
import { getSSOAuthErrorMessage } from 'pkg/sso';

import MaterialSymbol from 'components/material-symbols';

import { Spinner } from 'components/loaders/spinner';
import AuthGuardHeader from 'components/organization/auth_guard/header';

import Button from 'design/button';

import { setApiToken } from 'core/api_auth';

import * as css from './styles.css';

interface UnableToAuthProps {
	errorCode?: actions.sso.LoginErrorCodes;
}

function UnableToAuth({ errorCode }: UnableToAuthProps): JSX.Element {
	const t = useT();
	const message = getSSOAuthErrorMessage(t, errorCode);

	return (
		<div className={css.wrapper}>
			<AuthGuardHeader />
			<div className={css.content}>
				<div className={css.card}>
					<span className={css.login}>
						<MaterialSymbol variant="key" />
						{t('Unable to authenticate')}
					</span>
					<div>{message}</div>
				</div>
			</div>
		</div>
	);
}

interface SSOLandingProps {
	organizationId: number;
	code: string;
	state?: string;
}

function SSOLanding({
	organizationId,
	code,
	state,
}: SSOLandingProps): JSX.Element {
	const { refreshOrganizationIdentity } = useOrganizationIdentity();
	const [isLoading, setIsLoading] = useState(true);
	const [hasFailed, setHasFailed] = useState(false);
	const [errorCode, setErrorCode] = useState<actions.sso.LoginErrorCodes>(null);
	const parsedState = useMemo(() => parse(state), [state]);

	useEffect(() => {
		if (!code) {
			return;
		}
		const run = async () => {
			const [req, resp, errorResp] = await actions.sso.login({
				code,
				organizationId,
				state: state ?? '',
			});
			setIsLoading(false);

			if (!req.ok) {
				setErrorCode(errorResp?.error_code);
				setHasFailed(true);
				return;
			}

			actions.auth.updateAuthToken(
				{ accountToken: resp.accountToken, orgToken: resp.orgToken },
				organizationId
			);

			storeLoginInfo(
				resp.account,
				LoginTypes.SSO,
				parsedState?.identityProvider as unknown as models.identityProvider.ScrubbedIdentityProvider,
				organizationId
			);

			setApiToken(resp.orgToken);

			refreshOrganizationIdentity();
			window.history.pushState({}, '', '/');
		};

		run();
	}, [code, parsedState, organizationId]);

	if (isLoading) {
		return <Spinner center />;
	}

	if (!hasFailed) {
		return null;
	}

	return <UnableToAuth errorCode={errorCode} />;
}

interface OrganizationAuthGuardProps {
	organization: models.group.Group;
}

export default function OrganizationAuthGuard({
	organization,
}: OrganizationAuthGuardProps): JSX.Element {
	const t = useT();
	const orgHasSlug = !!organization?.slug;
	const query = useQueryParams();

	const { record: authOptions, isLoading } = useEndpoint<AuthOptions>(
		orgHasSlug && !query?.code
			? endpoints.Auth.Options(organization?.slug)
			: null
	);

	if (query?.code) {
		return (
			<SSOLanding
				organizationId={organization?.id}
				code={query.code as string}
				state={query.state as string}
			/>
		);
	}

	if (organization && !organization.slug) {
		return <UnableToAuth />;
	}

	const handleIdpClick = async (
		identityProvider: models.identityProvider.IdentityProvider
	) => {
		actions.sso.startLogin({ identityProvider });
	};

	if (isLoading) {
		return <Spinner center />;
	}

	return (
		<div className={css.wrapper}>
			<AuthGuardHeader />
			<div className={css.content}>
				<div className={css.card}>
					<span className={css.login}>
						<MaterialSymbol variant="key" />
						{t('Login with Single sign-on')}
					</span>
					<div>
						{t(
							'Your organization requires you to login using SSO in order to gain access to 360player.'
						)}
					</div>
					<div className={css.buttonWrapper}>
						{authOptions.identityProviders?.map((idp) => (
							<Button
								key={idp.id}
								label={t('Continue with {displayName}', {
									displayName: idp.displayName,
								})}
								primary
								onClick={() => handleIdpClick(idp)}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
